import { useEffect, useRef, useState } from 'react';
import ImportLink from '../components/import/ImportLink';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import PhotoSection from '../components/PhotoSection';
import PhotoPicker from '../components/PhotoPicker';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import DangerZone from '../components/DangerZone';
import { uploadPhotos } from '../utils/uploadPhotos';
import RecordAutocomplete from '../components/RecordAutocomplete';
import PlateFromSlantForm from '../components/PlateFromSlantForm';
import NewCultureWizard from '../components/NewCultureWizard';
import QRCodeLabel from '../components/QRCodeLabel';
import CreatedCodes from '../components/CreatedCodes';
import { generateSampleCode } from '../utils/generateSampleCode';
import { PLATE_STATUS_COLORS as STATUS_COLORS, SLANT_STATUS_COLORS } from '../constants/statusColors';
import { LAB_PHOTO_TYPES } from '../constants/photoTypes';
import { resolveLinkedRecordNav } from '../utils/linkedRecordNav';
import { captureDeviceGps, isValidCoordinate } from '../utils/geo';
import PlateConditionsFields, { EMPTY_CONDITIONS, conditionsPayload, conditionsSummary } from '../components/PlateConditionsFields';
import FieldError, { InvalidWithin, errorIdFor, invalidBorder, invalidProps } from '../components/FieldError';
import { collectErrors, hasErrors, required, useFieldErrors, validationMessage } from '../utils/formValidation';
import OrganicEmptyState from '../components/organic/OrganicEmptyState.jsx';
import OrganicLoader from '../components/organic/OrganicLoader.jsx';
import useFocusOnOpen from '../utils/useFocusOnOpen';
import MediumOptions from '../components/culture/MediumOptions';
import PlateStartFields, { EMPTY_START, PlateStartEditor, startBody } from '../components/culture/PlateStartFields';

// transferred_to_slants / transferred_to_plate are deliberately excluded — those are only
// ever set automatically as a consequence of the ready_to_transfer transfer flow below,
// never picked directly, so a plate's status can't claim a transfer that never happened.
const PLATE_STATUSES     = ['pending', 'ready_to_transfer', 'slow_growth', 'contaminated', 'in_rescue', 'failed'];
const CONTAMINATION_TYPES = [
  { value: 'bacterial_mold_mild',     label: 'Bacterial/mold – mild' },
  { value: 'bacterial_mold_moderate', label: 'Bacterial/mold – moderate' },
  { value: 'bacterial_mold_severe',   label: 'Bacterial/mold – severe' },
  { value: 'suspected_mold',          label: 'Suspected mold' },
];
// external_collection: a donor's culture, registered on the External tab first.
const ORIGINS            = ['field_collection', 'external_collection', 'independent_experiment'];

// A detail panel still loading: a growing tip beside the words. Not announced:
// the plate itself is already on screen, and four panels load at once.
const PanelLoader = ({ t }) => (
  <p style={dimStyle}>
    <OrganicLoader inline showLabel announce={false} label={t('common:loading')} slowLabel={t('common:stillWorking')} />
  </p>
);

const badge = (status, t) => {
  const style = STATUS_COLORS[status] || { background: 'var(--c-bg-muted)', color: 'var(--c-text-body-fg)' };
  return (
    <span style={{ ...style, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {status ? t(`plateStatus.${status}`) : ''}
    </span>
  );
};

const EMPTY_PLATE = {
  sample_id: '', external_id: '',
  origin: 'field_collection', protocol: '', protocol_other: '', temperature_c: 25,
  notes: '', isolate_code: '', gps_lat: '', gps_lng: '',
};

// routes/slants.py ISOLATE_CODE_RE: the same rule the server applies.
const ISOLATE_CODE_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{1,47}$/;

const EMPTY_CHECK = {
  status: '', contamination_type: '', colony_diameter_mm: '', notes: '', transfer_to: '',
  ...EMPTY_CONDITIONS,
};

const EMPTY_PARALLEL = { parallel_id: '' };

export default function Plates() {
  const { t } = useTranslation(['plates', 'common', 'culture']);
  const { user } = useAuth();
  // internal_expert is intentionally excluded here — spec: "No edit access to lab data (view only)"
  const isLab       = user?.roles?.some(r => ['collection_manager', 'lab_technician', 'student', 'lab_manager'].includes(r));
  const canDelete   = user?.roles?.some(r => ['collection_manager', 'lab_manager'].includes(r));
  const canEditNotes = user?.roles?.some(r => ['collection_manager', 'lab_technician', 'lab_manager'].includes(r));
  const canAssign = user?.roles?.some(r => ['collection_manager', 'lab_manager'].includes(r));

  const location = useLocation();
  const navigate = useNavigate();

  const [plates, setPlates]         = useState([]);
  const [protocols, setProtocols]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  // The form takes focus as it opens: its opener may be the empty state,
  // which goes away while the form is open.
  const newPlateHeadingRef = useFocusOnOpen(showForm);
  const [showFromSlant, setShowFromSlant] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(true);
  const [form, setForm]             = useState(EMPTY_PLATE);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError]   = useState('');
  const [photoWarn, setPhotoWarn]   = useState('');
  const [createdMsg, setCreatedMsg] = useState('');
  const [createdCaptions, setCreatedCaptions] = useState({});
  const [gpsState, setGpsState] = useState('');
  // The result of a create is shown at the top of the page; the button is at
  // the bottom of a long form, so the page is brought to it.
  const resultRef = useRef(null);
  // Codes of the plates just made, shown as printable QR labels.
  const [createdCodes, setCreatedCodes] = useState([]);
  // A specimen made from inside this form, for a culture that has no record yet.
  const EMPTY_SPECIMEN = { open: false, code: '', taxon: '', date: '', notes: '', saving: false, error: '' };
  const [specimen, setSpecimen] = useState(EMPTY_SPECIMEN);
  // Remounts the specimen picker so it refetches and shows the new record.
  const [pickerKey, setPickerKey] = useState(0);
  const [protocolSuggestion, setProtocolSuggestion] = useState(null);
  const [protocolLookupDone, setProtocolLookupDone] = useState(false);
  const [suggestionAccepted, setSuggestionAccepted] = useState(false);
  const [manualProtocol, setManualProtocol] = useState('');
  const [manualProtocolSelect, setManualProtocolSelect] = useState('');
  // Shared by the non-paired-origin protocol select (form.protocol/protocol_other)
  // and the paired-origin manual-fallback select (manualProtocolSelect/manualProtocol) —
  // the two are mutually exclusive within the same New Plate form (isPairedOrigin).
  const [addingNewProtocol, setAddingNewProtocol] = useState(false);
  const [selectedTaxonName, setSelectedTaxonName] = useState(null);
  const [awaitingCreationConflict, setAwaitingCreationConflict] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft]     = useState('');
  const [notesSaving, setNotesSaving]   = useState(false);
  const [editingTemperature, setEditingTemperature] = useState(false);
  const [temperatureDraft, setTemperatureDraft]     = useState('');
  const [temperatureSaving, setTemperatureSaving]   = useState(false);

  // Detail modal
  const [detail, setDetail]             = useState(null);

  // Checks
  const [checks, setChecks]             = useState([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [showCheckForm, setShowCheckForm] = useState(false);
  const [checkForm, setCheckForm]       = useState(EMPTY_CHECK);
  const [checkSaving, setCheckSaving]   = useState(false);
  const [checkError, setCheckError]     = useState('');
  const [transferMsg, setTransferMsg]   = useState('');
  const [deleteParallelTarget, setDeleteParallelTarget] = useState(null);
  const [deletingParallel, setDeletingParallel]         = useState(false);

  // Parallels
  const [parallels, setParallels]           = useState([]);
  const [parallelsLoading, setParallelsLoading] = useState(false);
  const [relatedPlates, setRelatedPlates]   = useState([]);
  const [relatedSlants, setRelatedSlants]   = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [showParallelForm, setShowParallelForm] = useState(false);
  const [parallelForm, setParallelForm]     = useState(EMPTY_PARALLEL);
  const [parallelSaving, setParallelSaving] = useState(false);
  const [parallelError, setParallelError]   = useState('');

  // Assigned To
  const [plateAssignments, setPlateAssignments]     = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [allUsers, setAllUsers]                     = useState([]);
  const [assignUserId, setAssignUserId]             = useState('');
  const [assignError, setAssignError]               = useState('');
  const [assignSaving, setAssignSaving]             = useState(false);

  // Path A — field sample intake (awaiting_creation plates)
  const [pathAPlates, setPathAPlates]           = useState([]);
  const [pathALoading, setPathALoading]         = useState(false);
  const [activatingSample, setActivatingSample] = useState(null);
  const [activateProtocolSuggestion, setActivateProtocolSuggestion] = useState(null);
  const [activateLookupDone, setActivateLookupDone] = useState(false);
  const [activateSuggestionAccepted, setActivateSuggestionAccepted] = useState(false);
  const [activateManualProtocol, setActivateManualProtocol] = useState('');
  const [activateManualProtocolSelect, setActivateManualProtocolSelect] = useState('');
  const [activateAddingNewProtocol, setActivateAddingNewProtocol] = useState(false);
  const [activateTemperature, setActivateTemperature] = useState(25);
  const [activateStart, setActivateStart] = useState(EMPTY_START);
  const [newPlateStart, setNewPlateStart] = useState(EMPTY_START);
  const [activateNotes, setActivateNotes] = useState('');
  const [activateError, setActivateError]       = useState('');
  const [activateSaving, setActivateSaving]     = useState(false);

  // Field-level errors: the New Plate form's, and Path A's protocol.
  const plateErrs = useFieldErrors();
  const activateErrs = useFieldErrors();
  // The props a New Plate field takes while it has an error.
  const plateFieldA11y = (field) => {
    const err = plateErrs.errors[field];
    return { ...invalidProps(errorIdFor(`plate-${field}`), !!err), style: { ...inputStyle, ...invalidBorder(!!err) } };
  };
  const plateFieldError = (field) => (
    <FieldError id={errorIdFor(`plate-${field}`)} testId={`plate-${field}-error`}>
      {validationMessage(t, plateErrs.errors[field])}
    </FieldError>
  );

  const load = () => {
    setLoading(true);
    api.get('/plates')
      .then(res => setPlates(res.data))
      .catch(err => setError(err.response?.data?.error || t('failedToLoad')))
      .finally(() => setLoading(false));
  };

  const loadPathA = () => {
    setPathALoading(true);
    api.get('/plates', { params: { status: 'awaiting_creation' } })
      .then(res => setPathAPlates(res.data))
      .catch(() => {})
      .finally(() => setPathALoading(false));
  };

  const loadProtocols = () => {
    api.get('/protocols')
      .then(res => setProtocols(res.data))
      .catch(() => {});
  };

  useEffect(() => { load(); loadPathA(); loadProtocols(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setTableExpanded(!(showForm || showFromSlant)); }, [showForm, showFromSlant]);

  // Arrived via a QR-code lookup — open that plate directly once the list has loaded,
  // then clear the nav state so back/forward doesn't reopen it.
  useEffect(() => {
    if (location.state?.openPlateId && plates.length > 0) {
      const match = plates.find(p => p.plate_id === location.state.openPlateId);
      if (match) openDetail(match);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, plates]);

  const activatePlates = async (sampleId) => {
    const activateManualProtocolValue = activateAddingNewProtocol ? activateManualProtocol.trim() : activateManualProtocolSelect;
    const usingAcceptedSuggestion = !!activateProtocolSuggestion && activateSuggestionAccepted;
    if (activateLookupDone && !usingAcceptedSuggestion) {
      const errors = collectErrors([
        ['activate_protocol', required(activateManualProtocolValue, 'pathA.protocolRequiredError')]]);
      if (hasErrors(errors)) { activateErrs.show(errors); return; }
    }
    activateErrs.reset();
    setActivateSaving(true);
    setActivateError('');
    if (activateAddingNewProtocol && activateManualProtocolValue) {
      // Best-effort: persist a newly-typed protocol so it shows up in the dropdown next time.
      api.post('/protocols', { name: activateManualProtocolValue }).then(loadProtocols).catch(() => {});
    }
    try {
      await api.put(`/plates/sample/${sampleId}/activate`, {
        manual_protocol: activateManualProtocolValue || undefined,
        accept_suggested_protocol: usingAcceptedSuggestion,
        temperature_c: parseFloat(activateTemperature) || 25,
        notes: activateNotes.trim() || undefined,
        ...startBody(activateStart),
      });
      setActivateStart(EMPTY_START);
      setActivatingSample(null);
      setActivateProtocolSuggestion(null);
      setActivateSuggestionAccepted(false);
      setActivateLookupDone(false);
      setActivateManualProtocol('');
      setActivateManualProtocolSelect('');
      setActivateAddingNewProtocol(false);
      setActivateTemperature(25);
      setActivateNotes('');
      loadPathA();
      load();
    } catch (err) {
      setActivateError(err.response?.data?.error || t('pathA.activateFailedError'));
    } finally {
      setActivateSaving(false);
    }
  };

  const fetchChecks = (plateId) => {
    setChecksLoading(true);
    api.get(`/plates/${plateId}/checks`)
      .then(res => setChecks(res.data))
      .catch(() => {})
      .finally(() => setChecksLoading(false));
  };

  const fetchParallels = (plateId) => {
    setParallelsLoading(true);
    api.get(`/plates/${plateId}/parallels`)
      .then(res => setParallels(res.data))
      .catch(() => {})
      .finally(() => setParallelsLoading(false));
  };

  const fetchRelated = (plateId) => {
    setRelatedLoading(true);
    api.get(`/plates/${plateId}/related`)
      .then(res => { setRelatedPlates(res.data.plates); setRelatedSlants(res.data.slants); })
      .catch(() => {})
      .finally(() => setRelatedLoading(false));
  };

  const fetchAssignments = (plateId) => {
    setAssignmentsLoading(true);
    api.get(`/plates/${plateId}/assignments`)
      .then(res => setPlateAssignments(res.data))
      .catch(() => {})
      .finally(() => setAssignmentsLoading(false));
  };

  const submitAssignment = () => {
    if (!assignUserId || !detail) return;
    setAssignSaving(true);
    setAssignError('');
    api.post(`/plates/${detail.plate_id}/assignments`, { user_id: assignUserId })
      .then(() => { setAssignUserId(''); fetchAssignments(detail.plate_id); })
      .catch(err => setAssignError(err.response?.data?.error || t('common:saveFailed')))
      .finally(() => setAssignSaving(false));
  };

  const removeAssignment = (assignmentId) => {
    api.delete(`/lab-assignments/${assignmentId}`)
      .then(() => setPlateAssignments(l => l.filter(a => a.assignment_id !== assignmentId)))
      .catch(() => {});
  };

  const openRelatedPlate = (plateId) => {
    const match = plates.find(p => p.plate_id === plateId);
    if (match) openDetail(match);
  };

  const openRelatedSlant = (slantId) => {
    const nav = resolveLinkedRecordNav('mushroom_bank', slantId);
    if (nav) navigate(nav.path, { state: nav.state });
  };

  const openDetail = (plate) => {
    setDetail(plate);
    setChecks([]);
    setShowCheckForm(false);
    setCheckForm(EMPTY_CHECK);
    setCheckError('');
    setTransferMsg('');
    setParallels([]);
    setShowParallelForm(false);
    setParallelForm(EMPTY_PARALLEL);
    setParallelError('');
    setEditingNotes(false);
    setNotesDraft('');
    setEditingTemperature(false);
    setTemperatureDraft('');
    setRelatedPlates([]);
    setRelatedSlants([]);
    fetchChecks(plate.plate_id);
    fetchParallels(plate.plate_id);
    fetchRelated(plate.plate_id);
    setPlateAssignments([]);
    setAssignUserId('');
    setAssignError('');
    if (canAssign) {
      fetchAssignments(plate.plate_id);
      if (allUsers.length === 0) {
        api.get('/users/names').then(res => setAllUsers(res.data)).catch(() => {});
      }
    }
  };

  const closeModal = () => {
    setDetail(null);
    setChecks([]);
    setParallels([]);
  };

  // New plate handlers
  // Which field's error an input clears when it changes.
  const ERROR_FIELD_OF = { protocol_other: 'protocol', gps_lng: 'gps_lat' };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    plateErrs.clear(ERROR_FIELD_OF[name] || name);
  };

  const handleOriginChange = (e) => {
    setForm(f => ({ ...f, origin: e.target.value, sample_id: '', external_id: '' }));
    plateErrs.reset();
    setSpecimen(EMPTY_SPECIMEN);
    setProtocolSuggestion(null);
    setProtocolLookupDone(false);
    setSuggestionAccepted(false);
    setManualProtocol('');
    setManualProtocolSelect('');
    setAddingNewProtocol(false);
    setSelectedTaxonName(null);
    setAwaitingCreationConflict(false);
  };

  /* A brand-new field specimen, made here so a culture with no record yet is
   * not stuck: the plate needs a specimen, and the specimen form used to be
   * somewhere else entirely. It is created as a draft through the ordinary
   * POST /collections, so the collector can complete it later in Collections. */
  const openSpecimen = () => setSpecimen({
    ...EMPTY_SPECIMEN, open: true,
    code: generateSampleCode(user?.initials),
    date: new Date().toISOString().slice(0, 10),
  });

  const createSpecimen = async () => {
    const code = specimen.code.trim();
    if (!code) { setSpecimen(s => ({ ...s, error: t('quickSpecimen.codeRequired') })); return; }
    setSpecimen(s => ({ ...s, saving: true, error: '' }));
    const taxon = specimen.taxon.trim();
    try {
      const { data } = await api.post('/collections', {
        qr_uid: code,
        collection_datetime: specimen.date ? `${specimen.date}T12:00:00` : null,
        current_taxon_name: taxon || null,
        current_rank: taxon ? 'species' : 'unknown',
        general_notes: specimen.notes.trim() || null,
      });
      setForm(f => ({ ...f, sample_id: data.sample_id }));
      plateErrs.clear('sample_id');
      setPickerKey(k => k + 1);
      setSelectedTaxonName(taxon || null);
      setAwaitingCreationConflict(false);
      lookupProtocol(taxon);
      setSpecimen({ ...EMPTY_SPECIMEN, created: data.qr_uid });
    } catch (err) {
      setSpecimen(s => ({ ...s, saving: false, error: err.response?.data?.error || t('quickSpecimen.createFailed') }));
    }
  };

  const lookupProtocol = (taxonName) => {
    setProtocolLookupDone(false);
    setSuggestionAccepted(false);
    setManualProtocol('');
    setManualProtocolSelect('');
    setAddingNewProtocol(false);
    if (!taxonName) { setProtocolSuggestion(null); setProtocolLookupDone(true); return; }
    api.get('/taxonomy/protocol-lookup', { params: { taxon_name: taxonName } })
      .then(res => setProtocolSuggestion(res.data.found ? res.data : null))
      .catch(() => setProtocolSuggestion(null))
      .finally(() => setProtocolLookupDone(true));
  };

  const saveNotes = () => {
    setNotesSaving(true);
    api.put(`/plates/${detail.plate_id}`, { notes: notesDraft })
      .then(() => {
        setDetail(d => d ? { ...d, notes: notesDraft } : d);
        setEditingNotes(false);
        load();
      })
      .catch(() => {})
      .finally(() => setNotesSaving(false));
  };

  const saveTemperature = () => {
    const value = parseFloat(temperatureDraft);
    if (isNaN(value)) return;
    setTemperatureSaving(true);
    api.put(`/plates/${detail.plate_id}`, { temperature_c: value })
      .then(() => {
        setDetail(d => d ? { ...d, temperature_c: value } : d);
        setEditingTemperature(false);
        load();
      })
      .catch(() => {})
      .finally(() => setTemperatureSaving(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const manualProtocolValue = addingNewProtocol ? manualProtocol.trim() : manualProtocolSelect;
    const protocolValue = addingNewProtocol ? form.protocol_other.trim() : form.protocol;
    const isolateCode = form.isolate_code.trim();
    const hasLat = form.gps_lat !== '' && form.gps_lat != null;
    const hasLng = form.gps_lng !== '' && form.gps_lng != null;
    const gpsBad = hasLat !== hasLng || (hasLat && !isValidCoordinate(form.gps_lat, form.gps_lng));
    // In the order the fields appear: focus goes to the first. Each is a rule
    // routes/plates.py::create_plate already enforces -- a field_collection
    // plate needs its specimen, a donor's plate its intake record, a plate
    // with no paired lookup its protocol -- said here beside the field.
    const errors = collectErrors([
      // The second plate's protocol, once the species lookup has answered and
      // its suggestion was not taken (the field is not on screen before).
      isPairedOrigin && protocolLookupDone && !(protocolSuggestion && suggestionAccepted)
        ? ['manual_protocol', required(manualProtocolValue, 'newPlate.protocolRequired')] : ['manual_protocol', null],
      ['protocol', isPairedOrigin ? null : required(protocolValue, 'newPlate.protocolRequired')],
      ['isolate_code', isolateCode && !ISOLATE_CODE_RE.test(isolateCode)
        ? { code: 'invalid', key: 'newPlate.isolateCodeInvalid' } : null],
      ['gps_lat', gpsBad ? { code: 'invalid', key: 'newPlate.gpsInvalid' } : null],
      ['external_id', form.origin === 'external_collection' ? required(form.external_id, 'newPlate.externalRequired') : null],
      ['sample_id', form.origin === 'field_collection' ? required(form.sample_id, 'newPlate.sampleRequired') : null],
    ]);
    if (hasErrors(errors)) {
      setFormError('');
      plateErrs.show(errors);
      return;
    }
    plateErrs.reset();
    // The specimen is chosen but its protocol lookup has not answered yet:
    // nothing to point at, so it is said once, beside the button.
    if (!form.origin || (isPairedOrigin && !protocolLookupDone)) {
      setFormError(t('newPlate.validationError'));
      return;
    }
    setSubmitting(true);
    setFormError('');
    const newlyTypedProtocol = isPairedOrigin ? manualProtocolValue : protocolValue;
    if (addingNewProtocol && newlyTypedProtocol) {
      // Best-effort: persist a newly-typed protocol so it shows up in the dropdown
      // next time. A 409 (already exists) is an expected, harmless outcome here.
      api.post('/protocols', { name: newlyTypedProtocol }).then(loadProtocols).catch(() => {});
    }
    createPlates({
      origin:      form.origin,
      protocol:    isPairedOrigin ? undefined : (protocolValue || null),
      temperature_c: parseFloat(form.temperature_c) || 25,
      manual_protocol: isPairedOrigin ? (manualProtocolValue || undefined) : undefined,
      accept_suggested_protocol: isPairedOrigin ? suggestionAccepted : undefined,
      sample_id:   form.origin === 'field_collection' ? (form.sample_id || null) : null,
      external_id: form.origin === 'external_collection' ? (form.external_id || null) : null,
      notes:       form.notes || null,
      isolate_code: isolateCode || undefined,
      gps_lat:     hasLat ? Number(form.gps_lat) : undefined,
      gps_lng:     hasLng ? Number(form.gps_lng) : undefined,
      ...startBody(newPlateStart),
    });
  };

  /* Creating and what follows it are separate failures. Once POST /plates has
   * answered, the plates exist: a photo that does not upload afterwards is a
   * warning about the photo, never "the plate could not be created" -- which
   * is what a single .catch over both used to say, and the person made the
   * plates again. */
  const createPlates = async (body) => {
    let res;
    try {
      res = await api.post('/plates', body);
    } catch (err) {
      setFormError(err.response?.data?.error || t('newPlate.createFailed'));
      setSubmitting(false);
      return;
    }
    try {
      const [first, second] = res.data.plates;
      let uploadResult = { succeeded: 0, failed: [] };
      if (pendingPhotos.length > 0) {
        // Filed under the plate, and under its specimen when it has one
        // (photos_check, 0048: a plate with no specimen may hold photos too).
        const anchor = { plate_id: first.plate_id,
          ...(first.sample_id ? { sample_id: first.sample_id } : {}),
          ...(first.external_id ? { external_id: first.external_id } : {}) };
        try {
          uploadResult = await uploadPhotos(pendingPhotos, anchor, selectedTaxonName, 'lab');
        } catch {
          uploadResult = { succeeded: 0, failed: pendingPhotos.map(p => ({ photoType: p.photoType || 'photo' })) };
        }
      }
      setShowForm(false); setForm(EMPTY_PLATE); setNewPlateStart(EMPTY_START); setPendingPhotos([]); setGpsState(''); load();
      setProtocolSuggestion(null);
      setProtocolLookupDone(false);
      setSuggestionAccepted(false);
      setManualProtocol('');
      setManualProtocolSelect('');
      setAddingNewProtocol(false);
      setSelectedTaxonName(null);
      setAwaitingCreationConflict(false);
      setCreatedCodes(res.data.plates.map(p => p.plate_code));
      setCreatedCaptions(Object.fromEntries(res.data.plates
        .filter(p => p.isolate_code).map(p => [p.plate_code, p.isolate_code])));
      setCreatedMsg(second
        ? t('newPlate.createdMsgPaired', { code1: first.plate_code, protocol1: first.protocol, code2: second.plate_code, protocol2: second.protocol })
        : t('newPlate.createdMsgSingle', { code: first.plate_code }));
      if (uploadResult.failed.length > 0) {
        const names = uploadResult.failed.map(f => f.photoType.replace(/_/g, ' ')).join(', ');
        setPhotoWarn(t('newPlate.photoUploadFailed', { count: uploadResult.failed.length, plateCode: first.plate_code, names }));
      }
      requestAnimationFrame(() => resultRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
    } finally {
      setSubmitting(false);
    }
  };

  const fillMyLocation = () => {
    setGpsState('locating');
    captureDeviceGps()
      .then(({ lat, lng }) => {
        setForm(f => ({ ...f, gps_lat: lat, gps_lng: lng }));
        // Both coordinates are now valid; the pair's message (kept on
        // gps_lat, see ERROR_FIELD_OF) goes with the bad values it named.
        plateErrs.clear('gps_lat');
        setGpsState('');
      })
      .catch(() => setGpsState('failed'));
  };

  // Check handlers
  const handleCheckChange = (e) => {
    const { name, value } = e.target;
    setCheckForm(f => ({ ...f, [name]: value }));
  };

  const isContaminated = (status) => ['contaminated', 'in_rescue'].includes(status);

  const submitCheck = (e) => {
    e.preventDefault();
    if (!checkForm.status) { setCheckError(t('detail.statusRequiredError')); return; }
    if (checkForm.status === 'ready_to_transfer' && !checkForm.transfer_to) {
      setCheckError(t('detail.transferToRequiredError'));
      return;
    }
    setCheckSaving(true);
    setCheckError('');
    api.post(`/plates/${detail.plate_id}/checks`, {
      ...checkForm,
      colony_diameter_mm: checkForm.colony_diameter_mm ? parseFloat(checkForm.colony_diameter_mm) : null,
      contamination_type: checkForm.status === 'contaminated' ? (checkForm.contamination_type || null) : null,
      transfer_to: checkForm.status === 'ready_to_transfer' ? checkForm.transfer_to : null,
      ...conditionsPayload(checkForm),
    })
      .then((res) => {
        setShowCheckForm(false);
        setCheckForm(EMPTY_CHECK);
        fetchChecks(detail.plate_id);
        // Refresh the plate list so status badge updates
        load();
        const transfer = res.data.transfer;
        const finalStatus = transfer
          ? (transfer.type === 'plate' ? 'transferred_to_plate' : 'transferred_to_slants')
          : checkForm.status;
        setDetail(p => p ? { ...p, status: finalStatus } : p);
        if (transfer?.type === 'plate') {
          setTransferMsg(t('detail.transferredToPlateMsg', { code: transfer.plate_code }));
        } else if (transfer?.type === 'slant') {
          setTransferMsg(t('detail.transferredToSlantMsg', { codes: transfer.slants.map(s => s.slant_code).join(', ') }));
        }
      })
      .catch(err => setCheckError(err.response?.data?.error || t('detail.checkSaveFailed')))
      .finally(() => setCheckSaving(false));
  };

  // Parallel handlers
  const submitParallel = (e) => {
    e.preventDefault();
    if (!parallelForm.parallel_id) { setParallelError(t('detail.parallelIdRequiredError')); return; }
    setParallelSaving(true);
    setParallelError('');
    api.post(`/plates/${detail.plate_id}/parallels`, parallelForm)
      .then(() => { setShowParallelForm(false); setParallelForm(EMPTY_PARALLEL); fetchParallels(detail.plate_id); })
      .catch(err => setParallelError(err.response?.data?.error || t('detail.parallelSaveFailed')))
      .finally(() => setParallelSaving(false));
  };

  const confirmRemoveParallel = () => {
    setDeletingParallel(true);
    api.delete(`/parallels/${deleteParallelTarget}`)
      .then(() => { setDeleteParallelTarget(null); fetchParallels(detail.plate_id); })
      .catch(() => setDeleteParallelTarget(null))
      .finally(() => setDeletingParallel(false));
  };

  // What the detail modal's DangerZone confirms; it shows a refusal itself.
  const deletePlate = () => api.delete(`/plates/${detail.plate_id}`)
    .then(() => { closeModal(); load(); });

  const isPairedOrigin = form.origin === 'field_collection';

  // The New Plate button's handler; the empty list offers the same thing.
  const toggleNewPlateForm = () => {
    setShowForm(s => !s); setFormError(''); plateErrs.reset(); setProtocolSuggestion(null); setSelectedTaxonName(null);
    setAwaitingCreationConflict(false); setManualProtocolSelect(''); setAddingNewProtocol(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>{t('pageTitle')}</h2>
        {isLab && (
          <button onClick={() => { setShowWizard(s => !s); setShowForm(false); setShowFromSlant(false); }}
            style={btnStyle('var(--c-primary)')} title={t('tip.newCulture')} data-testid="new-culture-btn">
            {showWizard ? t('common:cancel') : t('newCultureBtn')}
          </button>
        )}
        {isLab && (
          <button onClick={toggleNewPlateForm} style={btnStyle('var(--c-text-muted)')} title={t('tip.newPlate')}>
            {showForm ? t('common:cancel') : t('newPlateBtn')}
          </button>
        )}
        <ImportLink kind="plates" />
        <button onClick={() => setShowFromSlant(s => !s)} style={btnStyle('var(--c-info)')} title={t('tip.fromSlant')}>
          {showFromSlant ? t('common:cancel') : t('fromSlantBtn')}
        </button>
      </div>

      <div ref={resultRef} />
      {photoWarn && (
        <div data-testid="photo-warn" style={{ background: 'var(--c-warning-soft-2)', border: '1px solid var(--c-warning)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, fontSize: 14, color: 'var(--c-warning-text-fg)' }}>
          <span>⚠ {photoWarn}</span>
          <button onClick={() => setPhotoWarn('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-warning-text-fg)', fontSize: 18, padding: 0, flexShrink: 0, lineHeight: 1 }} title={t('tip.dismiss')}>×</button>
        </div>
      )}

      {createdMsg && (
        <div data-testid="created-msg" style={{ background: 'var(--c-primary-soft)', border: '1px solid var(--c-primary-border-2)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, fontSize: 14, color: 'var(--c-success-2-fg)' }}>
          <span>✓ {createdMsg}</span>
          <button onClick={() => setCreatedMsg('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-success-2-fg)', fontSize: 18, padding: 0, flexShrink: 0, lineHeight: 1 }} title={t('tip.dismiss')}>×</button>
        </div>
      )}

      <CreatedCodes codes={createdCodes} captions={createdCaptions} onDismiss={() => setCreatedCodes([])} />

      {/* Path A — Field Sample Intake: plates awaiting physical creation */}
      {isLab && (() => {
        const groups = Object.values(
          pathAPlates.reduce((acc, p) => {
            if (!p.sample_id) return acc;
            if (!acc[p.sample_id]) acc[p.sample_id] = { sample_id: p.sample_id, taxon_name: p.taxon_name, plates: [] };
            acc[p.sample_id].plates.push(p);
            return acc;
          }, {})
        );
        return (
          <div style={{ border: '1px solid var(--c-purple-border-2)', borderRadius: 8, padding: 16, marginBottom: 20, background: 'var(--c-purple-soft-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: groups.length > 0 ? 12 : 0 }}>
              <strong style={{ fontSize: 14, color: 'var(--c-purple-3-fg)' }}>{t('pathA.title')}</strong>
              {pathALoading ? (
                <span style={{ fontSize: 12, color: 'var(--c-text-faint-fg)' }}>
                  {/* Not announced: the page's own loader speaks for both. */}
                  <OrganicLoader inline showLabel announce={false} label={t('pathA.loading')} slowLabel={t('common:stillWorking')} />
                </span>
              ) : (
                <span style={{ background: groups.length > 0 ? 'var(--c-purple)' : 'var(--c-border-strong)', color: 'var(--c-on-accent)', borderRadius: 10, fontSize: 11, padding: '1px 8px', fontWeight: 600 }}>
                  {t('pathA.awaiting', { count: groups.length })}
                </span>
              )}
            </div>
            {!pathALoading && groups.length === 0 && (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('pathA.noneAwaiting')}</p>
            )}
            {groups.map(group => (
              <div key={group.sample_id} style={{ background: 'var(--c-surface)', border: '1px solid var(--c-purple-border)', borderRadius: 6, padding: '10px 14px', marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontSize: 13 }}>
                    <span style={{ fontStyle: 'italic', fontWeight: 500 }} dir="ltr">{group.taxon_name || t('pathA.unknownTaxon')}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--c-text-faint-fg)', marginInlineStart: 8 }} dir="ltr">{group.sample_id.slice(0, 8)}…</span>
                    <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', marginInlineStart: 10 }}>
                      {t('pathA.plateCount', { count: group.plates.length, codes: group.plates.map(p => `#${p.plate_code}`).join(', ') })}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      const next = activatingSample === group.sample_id ? null : group.sample_id;
                      setActivatingSample(next);
                      setActivateProtocolSuggestion(null);
                      setActivateSuggestionAccepted(false);
                      setActivateLookupDone(false);
                      setActivateManualProtocol('');
                      setActivateManualProtocolSelect('');
                      setActivateAddingNewProtocol(false);
                      setActivateTemperature(25);
                      setActivateNotes('');
                      setActivateError('');
                      activateErrs.reset();
                      if (next) {
                        api.get('/taxonomy/protocol-lookup', { params: { taxon_name: group.taxon_name } })
                          .then(res => setActivateProtocolSuggestion(res.data.found ? res.data : null))
                          .catch(() => setActivateProtocolSuggestion(null))
                          .finally(() => setActivateLookupDone(true));
                      }
                    }}
                    style={btnStyle(activatingSample === group.sample_id ? 'var(--c-text-muted)' : 'var(--c-purple)')} title={t('tip.pathACreate')}>
                    {activatingSample === group.sample_id ? t('pathA.cancel') : t('pathA.createPlatesBtn')}
                  </button>
                </div>

                {activatingSample === group.sample_id && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--c-purple-border)' }}>
                    {activateError && <p role="alert" style={{ ...errStyle, marginBottom: 8 }}>{activateError}</p>}
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('pathA.plate1Note')}</p>
                    {!activateLookupDone && (
                      <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--c-text-faint-fg)' }}>
                        <OrganicLoader inline showLabel label={t('pathA.protocolLookupLoading')} slowLabel={t('common:stillWorking')} />
                      </p>
                    )}
                    {activateLookupDone && activateProtocolSuggestion && (
                      <div style={{ background: 'var(--c-info-soft-2)', border: '1px solid var(--c-info-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 10, fontSize: 13, color: 'var(--c-info-3-fg)' }}>
                        <div>
                          {t('pathA.protocolFoundMsg', {
                            level: t(`common:rank.${activateProtocolSuggestion.level}`),
                            matchedName: activateProtocolSuggestion.matched_name,
                            protocol: activateProtocolSuggestion.protocol,
                          })}
                        </div>
                        {activateProtocolSuggestion.temperature_c !== null ? (
                          <div>{t('pathA.suggestedTemp', { temp: activateProtocolSuggestion.temperature_c })}</div>
                        ) : activateProtocolSuggestion.temperature_raw ? (
                          <div style={{ color: 'var(--c-warning-text-fg)' }}>
                            {t('pathA.suggestedTempRange', { tempRaw: activateProtocolSuggestion.temperature_raw })}
                          </div>
                        ) : null}
                        {activateProtocolSuggestion.source && (
                          <div style={{ fontSize: 11, color: 'var(--c-info-4-fg)', marginTop: 4 }}>
                            {t(`pathA.sourceLabel.${activateProtocolSuggestion.source}`)}
                          </div>
                        )}
                        {activateProtocolSuggestion.basis && (
                          <details style={{ fontSize: 11, color: 'var(--c-info-4-fg)', marginTop: 4 }}>
                            <summary style={{ cursor: 'pointer' }}>{t('pathA.basisSummary')}</summary>
                            <div style={{ marginTop: 4 }}>{activateProtocolSuggestion.basis}</div>
                          </details>
                        )}
                        {activateSuggestionAccepted ? (
                          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 600 }}>{t('pathA.usingSuggested')}</span>
                            <button type="button" onClick={() => setActivateSuggestionAccepted(false)} style={smallBtn('var(--c-text-muted)')} title={t('tip.changeSuggestion')}>
                              {t('pathA.changeBtn')}
                            </button>
                          </div>
                        ) : (
                          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                            <button
                              type="button"
                              onClick={() => {
                                setActivateSuggestionAccepted(true);
                                // Also clear any manual-protocol entry the tech may have
                                // started before deciding to accept the suggestion instead —
                                // Task 7's review caught this exact gap in the New Plate form
                                // (a stale manual entry could still get POSTed to the shared
                                // protocols table, or ride along in the submit payload
                                // alongside accept_suggested_protocol=true). Same fix here,
                                // applied up front rather than discovered in review.
                                setActivateManualProtocol('');
                                setActivateManualProtocolSelect('');
                                setActivateAddingNewProtocol(false);
                                activateErrs.reset();
                                // Same reasoning as Task 7's New Plate form: only pre-fill
                                // when the source parsed to a clean number. The Path A
                                // temperature field is always visible/editable already
                                // (unlike the New Plate form's field, which Task 7 hides
                                // once resolved), so there's no separate visibility toggle
                                // needed here — just make sure the right value is in it.
                                if (activateProtocolSuggestion.temperature_c !== null) {
                                  setActivateTemperature(activateProtocolSuggestion.temperature_c);
                                }
                              }}
                              style={smallBtn('var(--c-primary)')} title={t('tip.acceptSuggestion')}>
                              {t('pathA.acceptBtn')}
                            </button>
                            <button type="button" onClick={() => setActivateSuggestionAccepted(false)} style={smallBtn('var(--c-text-muted)')} title={t('tip.enterManually')}>
                              {t('pathA.enterManuallyBtn')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {activateLookupDone && !activateProtocolSuggestion && (
                      <div style={{ background: 'var(--c-warning-soft-2)', border: '1px solid var(--c-warning)', borderRadius: 8, padding: '10px 14px', marginBottom: 10, fontSize: 13, color: 'var(--c-warning-text-fg)' }}>
                        {t('pathA.protocolNotFoundMsg')}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <label style={labelStyle}>
                        {t('pathA.temperatureLabel')}
                        <input
                          type="number"
                          value={activateTemperature}
                          onChange={e => setActivateTemperature(e.target.value)}
                          style={inputStyle}
                        title={t('tip.temperatureOther')} />
                      </label>
                      <PlateStartFields value={activateStart} labelStyle={labelStyle} inputStyle={inputStyle} help={false}
                                        testIdPrefix="activate-start"
                                        onChange={patch => setActivateStart(s => ({ ...s, ...patch }))} />
                      {activateLookupDone && !(activateProtocolSuggestion && activateSuggestionAccepted) && (() => {
                        const err = activateErrs.errors.activate_protocol;
                        const id = `activate-protocol-${group.sample_id}`;
                        const a11y = { id, ...invalidProps(errorIdFor(id), !!err), 'aria-required': true };
                        const boxStyle = { ...inputStyle, flex: 1, ...invalidBorder(!!err) };
                        return (
                          <div style={labelStyle} data-field="activate_protocol">
                            <label htmlFor={id}>{t('pathA.manualProtocolLabel')}</label>
                            {activateAddingNewProtocol ? (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <input
                                  {...a11y}
                                  value={activateManualProtocol}
                                  onChange={e => { setActivateManualProtocol(e.target.value); activateErrs.clear('activate_protocol'); }}
                                  placeholder={t('pathA.manualProtocolPlaceholder')}
                                  style={boxStyle}
                                title={t('tip.newProtocolName')} />
                                <button type="button" onClick={() => { setActivateAddingNewProtocol(false); setActivateManualProtocol(''); }} style={smallBtn('var(--c-text-muted)')} title={t('tip.cancelNewProtocol')}>
                                  {t('common:cancel')}
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <select {...a11y} value={activateManualProtocolSelect} onChange={e => { setActivateManualProtocolSelect(e.target.value); activateErrs.clear('activate_protocol'); }} style={boxStyle} title={t('tip.protocolSelect')}
                                        data-testid="activate-protocol">
                                  <option value="">{t('selectProtocol')}</option>
                                  <MediumOptions protocols={protocols} />
                                </select>
                                <button type="button" onClick={() => setActivateAddingNewProtocol(true)} style={smallBtn('var(--c-info)')} title={t('tip.addProtocol')}>
                                  {t('newProtocolBtn')}
                                </button>
                              </div>
                            )}
                            <FieldError id={errorIdFor(id)} testId="activate-protocol-error">{validationMessage(t, err)}</FieldError>
                          </div>
                        );
                      })()}
                      <label style={{ ...labelStyle, flexBasis: '100%' }}>
                        {t('pathA.notesLabel')}
                        <textarea
                          value={activateNotes}
                          onChange={e => setActivateNotes(e.target.value)}
                          rows={2}
                          style={inputStyle}
                        title={t('tip.notes')} />
                      </label>
                      <button
                        onClick={() => activatePlates(group.sample_id)}
                        disabled={activateSaving || !activateLookupDone}
                        style={{ ...btnStyle('var(--c-primary)'), alignSelf: 'flex-end' }} title={t('tip.confirmCreation')}>
                        {activateSaving ? t('pathA.saving') : t('pathA.confirmCreationBtn')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {showWizard && (
        <NewCultureWizard
          protocols={protocols}
          onCancel={() => setShowWizard(false)}
          onCreated={({ plates: made, warnings, isolateCode }) => {
            setShowWizard(false);
            load();
            const codes = made.map(p => p.plate_code);
            setCreatedCodes(codes);
            setCreatedCaptions(isolateCode ? Object.fromEntries(codes.map(c => [c, isolateCode])) : {});
            setCreatedMsg(t('culture:save.created', { codes: codes.join(', ') }));
            setPhotoWarn(warnings.length ? warnings.join(' ') : '');
            requestAnimationFrame(() => resultRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
          }}
        />
      )}

      {showFromSlant && (
        <PlateFromSlantForm
          protocols={protocols}
          onCancel={() => setShowFromSlant(false)}
          onCreated={(plate) => {
            setShowFromSlant(false);
            load();
            setCreatedMsg(t('fromSlant.createdMsg', { code: plate.plate_code, slantCode: plate.source_slant_code }));
            setCreatedCodes([plate.plate_code]);
          }}
        />
      )}

      {showForm && (
        <form onSubmit={handleSubmit} style={formStyle} ref={plateErrs.containerRef} data-testid="new-plate-form">
          <h3 ref={newPlateHeadingRef} tabIndex={-1} style={{ marginTop: 0 }} data-testid="new-plate-title">{t('newPlate.title')}</h3>
          <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '0 0 4px' }}>
            {t('newPlate.desc')}
          </p>
          <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '0 0 12px' }}>{t('common:validation.requiredNote')}</p>
          {/* Also said beside the submit button (plate-form-error), which is
              the one announced: once is enough. */}
          {formError && <p style={{ ...errStyle, margin: '0 0 12px', fontSize: 14 }}>{formError}</p>}
          {isPairedOrigin && (
            <>
              <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '0 0 8px' }}>{t('newPlate.plate1Note')}</p>
              {form.sample_id || form.external_id ? (
                !protocolLookupDone ? (
                  <p style={{ fontSize: 12, color: 'var(--c-text-faint-fg)', margin: '0 0 12px' }}>
                    <OrganicLoader inline showLabel label={t('newPlate.protocolLookupLoading')} slowLabel={t('common:stillWorking')} />
                  </p>
                ) : protocolSuggestion ? (
                  <div style={{ background: 'var(--c-info-soft-2)', border: '1px solid var(--c-info-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: 'var(--c-info-3-fg)' }}>
                    <div>
                      {t('newPlate.protocolFoundMsg', {
                        level: t(`common:rank.${protocolSuggestion.level}`),
                        matchedName: protocolSuggestion.matched_name,
                        protocol: protocolSuggestion.protocol,
                      })}
                    </div>
                    {protocolSuggestion.temperature_c !== null ? (
                      <div>{t('newPlate.suggestedTemp', { temp: protocolSuggestion.temperature_c })}</div>
                    ) : protocolSuggestion.temperature_raw ? (
                      <div style={{ color: 'var(--c-warning-text-fg)' }}>
                        {t('newPlate.suggestedTempRange', { tempRaw: protocolSuggestion.temperature_raw })}
                      </div>
                    ) : null}
                    {protocolSuggestion.source && (
                      <div style={{ fontSize: 11, color: 'var(--c-info-4-fg)', marginTop: 4 }}>
                        {t(`newPlate.sourceLabel.${protocolSuggestion.source}`)}
                      </div>
                    )}
                    {protocolSuggestion.basis && (
                      <details style={{ fontSize: 11, color: 'var(--c-info-4-fg)', marginTop: 4 }}>
                        <summary style={{ cursor: 'pointer' }}>{t('newPlate.basisSummary')}</summary>
                        <div style={{ marginTop: 4 }}>{protocolSuggestion.basis}</div>
                      </details>
                    )}
                    {suggestionAccepted ? (
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600 }}>{t('newPlate.usingSuggested')}</span>
                        <button type="button" onClick={() => setSuggestionAccepted(false)} style={smallBtn('var(--c-text-muted)')} title={t('tip.changeSuggestion')}>
                          {t('newPlate.changeBtn')}
                        </button>
                      </div>
                    ) : (
                      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => {
                            setSuggestionAccepted(true);
                            setManualProtocol('');
                            setManualProtocolSelect('');
                            setAddingNewProtocol(false);
                            // Only pre-fill the number field when the source parsed to a
                            // clean number — culture_temperature can be a free-text range
                            // (e.g. "20-25"), in which case temperature_c is null and the
                            // field stays as-is for the tech to fill in themselves (Step 5
                            // keeps the field visible whenever temperature_c is null, even
                            // after accepting).
                            if (protocolSuggestion.temperature_c !== null) {
                              setForm(f => ({ ...f, temperature_c: protocolSuggestion.temperature_c }));
                            }
                          }}
                          style={smallBtn('var(--c-primary)')} title={t('tip.acceptSuggestion')}>
                          {t('newPlate.acceptBtn')}
                        </button>
                        <button type="button" onClick={() => setSuggestionAccepted(false)} style={smallBtn('var(--c-text-muted)')} title={t('tip.enterManually')}>
                          {t('newPlate.enterManuallyBtn')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ background: 'var(--c-warning-soft-2)', border: '1px solid var(--c-warning)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: 'var(--c-warning-text-fg)' }}>
                    {t('newPlate.protocolNotFoundMsg')}
                  </div>
                )
              ) : null}
            </>
          )}
          <div style={gridStyle}>
            <label style={labelStyle}>
              {t('newPlate.originLabel')}
              <select name="origin" value={form.origin} onChange={handleOriginChange} style={inputStyle} title={t('tip.origin')}>
                {ORIGINS.map(o => <option key={o} value={o}>{t(`origin.${o}`)}</option>)}
              </select>
            </label>
            {isPairedOrigin ? (
              <>
                {/* Hide the temperature field only once acceptance has resolved it to an
                    actual number (pre-filled by the Accept button above). If the source
                    value was a free-text range (temperature_c null), the field stays
                    visible and required even after accepting — a range isn't a value
                    plate_culture.temperature_c (NUMERIC) can store, so a human still has
                    to pick one. */}
                {!(protocolSuggestion && suggestionAccepted && protocolSuggestion.temperature_c !== null) && (
                  <label style={labelStyle}>
                    {t('newPlate.temperatureLabelPaired')}
                    <input name="temperature_c" type="number" value={form.temperature_c} onChange={handleChange} style={inputStyle} title={t('tip.temperatureOther')} />
                  </label>
                )}
                {protocolLookupDone && !(protocolSuggestion && suggestionAccepted) && (
                  <div style={labelStyle} data-field="manual_protocol">
                    <label htmlFor="plate-manual_protocol">{t('newPlate.manualProtocolLabel')}</label>
                    {addingNewProtocol ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input id="plate-manual_protocol" aria-required="true" {...plateFieldA11y('manual_protocol')}
                               value={manualProtocol} onChange={e => { setManualProtocol(e.target.value); plateErrs.clear('manual_protocol'); }}
                               placeholder={t('newPlate.manualProtocolPlaceholder')} style={{ ...plateFieldA11y('manual_protocol').style, flex: 1 }} title={t('tip.newProtocolName')} />
                        <button type="button" onClick={() => { setAddingNewProtocol(false); setManualProtocol(''); }} style={smallBtn('var(--c-text-muted)')} title={t('tip.cancelNewProtocol')}>
                          {t('common:cancel')}
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select id="plate-manual_protocol" aria-required="true" {...plateFieldA11y('manual_protocol')}
                                value={manualProtocolSelect} onChange={e => { setManualProtocolSelect(e.target.value); plateErrs.clear('manual_protocol'); }}
                                style={{ ...plateFieldA11y('manual_protocol').style, flex: 1 }} title={t('tip.protocolSelect')}>
                          <option value="">{t('selectProtocol')}</option>
                          <MediumOptions protocols={protocols} />
                        </select>
                        <button type="button" onClick={() => setAddingNewProtocol(true)} style={smallBtn('var(--c-info)')} title={t('tip.addProtocol')}>
                          {t('newProtocolBtn')}
                        </button>
                      </div>
                    )}
                    {plateFieldError('manual_protocol')}
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={labelStyle} data-field="protocol">
                  <label htmlFor="plate-protocol">{t('newPlate.protocolLabel')}</label>
                  {addingNewProtocol ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input id="plate-protocol" aria-required="true" {...plateFieldA11y('protocol')}
                             name="protocol_other" value={form.protocol_other} onChange={handleChange} placeholder={t('newPlate.specifyProtocolPlaceholder')}
                             style={{ ...plateFieldA11y('protocol').style, flex: 1 }} title={t('tip.newProtocolName')} />
                      <button type="button" onClick={() => { setAddingNewProtocol(false); setForm(f => ({ ...f, protocol_other: '' })); }} style={smallBtn('var(--c-text-muted)')} title={t('tip.cancelNewProtocol')}>
                        {t('common:cancel')}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select id="plate-protocol" aria-required="true" {...plateFieldA11y('protocol')}
                              name="protocol" value={form.protocol} onChange={handleChange}
                              style={{ ...plateFieldA11y('protocol').style, flex: 1 }} title={t('tip.protocolSelect')}>
                        <option value="">{t('selectProtocol')}</option>
                        <MediumOptions protocols={protocols} />
                      </select>
                      <button type="button" onClick={() => setAddingNewProtocol(true)} style={smallBtn('var(--c-info)')} title={t('tip.addProtocol')}>
                        {t('newProtocolBtn')}
                      </button>
                    </div>
                  )}
                  {plateFieldError('protocol')}
                </div>
                <label style={labelStyle}>
                  {t('newPlate.temperatureLabel')}
                  <input name="temperature_c" type="number" value={form.temperature_c} onChange={handleChange} style={inputStyle} title={t('tip.temperature')} />
                </label>
              </>
            )}
            <div style={{ ...labelStyle, gridColumn: 'span 2' }} data-field="isolate_code">
              <label htmlFor="plate-isolate_code">{t('newPlate.isolateCodeLabel')}</label>
              <input id="plate-isolate_code" name="isolate_code" value={form.isolate_code} onChange={handleChange}
                     {...plateFieldA11y('isolate_code')}
                     dir="ltr" maxLength={48} placeholder={t('newPlate.isolateCodePlaceholder')}
                     title={t('tip.isolateCode')} data-testid="plate-isolate-code" />
              {plateFieldError('isolate_code')}
            </div>
            <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }} data-field="gps_lat">
              <label style={{ ...labelStyle, flex: '1 1 140px' }}>
                {t('newPlate.gpsLat')}
                <input name="gps_lat" type="number" step="any" value={form.gps_lat} onChange={handleChange}
                       {...plateFieldA11y('gps_lat')}
                       dir="ltr" title={t('tip.gpsLat')} data-testid="plate-gps-lat" />
              </label>
              <label style={{ ...labelStyle, flex: '1 1 140px' }}>
                {t('newPlate.gpsLng')}
                <input name="gps_lng" type="number" step="any" value={form.gps_lng} onChange={handleChange}
                       {...plateFieldA11y('gps_lat')}
                       dir="ltr" title={t('tip.gpsLng')} data-testid="plate-gps-lng" />
              </label>
              <button type="button" onClick={fillMyLocation} disabled={gpsState === 'locating'}
                      style={{ ...btnStyle('var(--c-info)'), marginBottom: 12 }} title={t('tip.useMyLocation')}
                      data-testid="plate-gps-here">
                {gpsState === 'locating' ? t('newPlate.locating') : t('newPlate.useMyLocation')}
              </button>
              {gpsState === 'failed' && <span style={{ color: 'var(--c-danger-fg)', fontSize: 13, marginBottom: 12 }}>{t('newPlate.gpsFailed')}</span>}
              <div style={{ flexBasis: '100%', marginTop: -8 }}>{plateFieldError('gps_lat')}</div>
            </div>
            <PlateStartFields value={newPlateStart} labelStyle={labelStyle} inputStyle={inputStyle} testIdPrefix="new-plate-start"
                              onChange={patch => setNewPlateStart(s => ({ ...s, ...patch }))} />
            <label style={{ ...labelStyle, gridColumn: 'span 2' }}>
              {t('newPlate.notesLabel')}
              <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} style={inputStyle} title={t('tip.notes')} />
            </label>
            {form.origin === 'external_collection' && (
              // The message sits outside the <label>, which names the picker:
              // inside, it would become part of that name.
              <div style={fieldStackStyle}>
                <label style={labelStyle}>
                  {t('newPlate.externalIdLabel')}
                  <InvalidWithin required invalid={!!plateErrs.errors.external_id} errorId={errorIdFor('plate-external_id')} data-field="external_id">
                    <RecordAutocomplete
                      fetchUrl="/external"
                      getLabel={r => r.qr_uid || r.external_id.slice(0, 8)}
                      getSecondary={r => r.taxon_name}
                      getValue={r => r.external_id}
                      value={form.external_id}
                      onChange={val => { setForm(f => ({ ...f, external_id: val })); if (val) plateErrs.clear('external_id'); }}
                      placeholder={t('newPlate.externalSearchPlaceholder')}
                    />
                  </InvalidWithin>
                </label>
                {plateFieldError('external_id')}
              </div>
            )}
            {form.origin === 'field_collection' && (
              <div style={{ gridColumn: 'span 2', display: 'grid', gap: 6 }}>
                {specimen.open ? (
                  <fieldset style={{ border: '1px solid var(--c-border)', borderRadius: 8, padding: 10, display: 'grid', gap: 8 }}
                            data-testid="quick-specimen">
                    <legend>{t('quickSpecimen.title')}</legend>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--c-text-muted)' }}>{t('quickSpecimen.hint')}</p>
                    <label style={labelStyle}>{t('quickSpecimen.code')}
                      <input value={specimen.code} dir="ltr" style={inputStyle} data-testid="quick-specimen-code"
                             title={t('quickSpecimen.codeTip')}
                             onChange={e => setSpecimen(sp => ({ ...sp, code: e.target.value }))} />
                    </label>
                    <label style={labelStyle}>{t('quickSpecimen.taxon')}
                      <input value={specimen.taxon} style={inputStyle} data-testid="quick-specimen-taxon"
                             title={t('quickSpecimen.taxonTip')}
                             onChange={e => setSpecimen(sp => ({ ...sp, taxon: e.target.value }))} />
                    </label>
                    <label style={labelStyle}>{t('quickSpecimen.date')}
                      <input type="date" value={specimen.date} style={inputStyle} title={t('quickSpecimen.dateTip')}
                             onChange={e => setSpecimen(sp => ({ ...sp, date: e.target.value }))} />
                    </label>
                    <label style={labelStyle}>{t('quickSpecimen.notes')}
                      <input value={specimen.notes} style={inputStyle} title={t('quickSpecimen.notesTip')}
                             onChange={e => setSpecimen(sp => ({ ...sp, notes: e.target.value }))} />
                    </label>
                    {specimen.error && <p role="alert" style={{ color: 'var(--c-danger-fg)', margin: 0 }}>{specimen.error}</p>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={createSpecimen} disabled={specimen.saving} style={smallBtn('var(--c-primary)')}
                              data-testid="quick-specimen-create" title={t('quickSpecimen.createTip')}>
                        {specimen.saving ? t('newPlate.saving') : t('quickSpecimen.create')}
                      </button>
                      <button type="button" onClick={() => setSpecimen(EMPTY_SPECIMEN)} style={smallBtn('var(--c-text-muted)')}
                              title={t('quickSpecimen.cancelTip')}>
                        {t('common:cancel')}
                      </button>
                    </div>
                  </fieldset>
                ) : (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" onClick={openSpecimen} style={smallBtn('var(--c-info)')}
                            data-testid="quick-specimen-open" title={t('quickSpecimen.openTip')}>
                      {t('quickSpecimen.open')}
                    </button>
                    {specimen.created && (
                      <span role="status" data-testid="quick-specimen-created">
                        {t('quickSpecimen.created', { code: specimen.created })}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            {form.origin === 'field_collection' && (
              <div style={fieldStackStyle}>
                <label style={labelStyle}>
                  {t('newPlate.sampleIdLabel')}
                  <InvalidWithin required invalid={!!plateErrs.errors.sample_id} errorId={errorIdFor('plate-sample_id')} data-field="sample_id">
                    <RecordAutocomplete
                      key={pickerKey}
                      fetchUrl="/collections"
                      getLabel={r => r.qr_uid || r.sample_id.slice(0, 8)}
                      getSecondary={r => r.current_taxon_name}
                      getValue={r => r.sample_id}
                      value={form.sample_id}
                      onChange={val => { setForm(f => ({ ...f, sample_id: val })); if (val) plateErrs.clear('sample_id'); if (!val) { setProtocolSuggestion(null); setProtocolLookupDone(false); setManualProtocol(''); setManualProtocolSelect(''); setAddingNewProtocol(false); setSelectedTaxonName(null); setAwaitingCreationConflict(false); } }}
                      onSelect={record => {
                        lookupProtocol(record.current_taxon_name);
                        setSelectedTaxonName(record.current_taxon_name || null);
                        setAwaitingCreationConflict(pathAPlates.some(p => p.sample_id === record.sample_id));
                      }}
                      placeholder={t('newPlate.searchPlaceholder')}
                    />
                  </InvalidWithin>
                </label>
                {plateFieldError('sample_id')}
              </div>
            )}
            {awaitingCreationConflict && (
              <div style={{ gridColumn: 'span 2', background: 'var(--c-purple-soft)', border: '1px solid var(--c-purple-border-3)', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: 'var(--c-purple-2-fg)' }}>
                {t('newPlate.awaitingCreationWarning')}
              </div>
            )}
          </div>
          <PhotoPicker value={pendingPhotos} onChange={setPendingPhotos} photoTypes={LAB_PHOTO_TYPES} />
          {formError && <p role="alert" data-testid="plate-form-error" style={{ color: 'var(--c-danger-fg)', margin: '12px 0 0' }}>{formError}</p>}
          <button type="submit" disabled={submitting || awaitingCreationConflict} style={{ ...btnStyle('var(--c-primary)'), marginTop: 14 }} title={t('tip.createPlate')}>
            {submitting ? t('newPlate.saving') : t('newPlate.createPlateBtn')}
          </button>
        </form>
      )}

      {loading && (
        <OrganicLoader block motif="agar" label={t('common:loading')} slowLabel={t('common:stillWorking')} />
      )}
      {error && <p role="alert" style={{ ...errStyle, fontSize: 14 }}>{error}</p>}

      {/* Nothing yet. Not shown while the New Plate form is open: that is
          already the thing it would suggest. */}
      {!loading && !error && plates.length === 0 && !showForm && (
        <OrganicEmptyState kind="plates" title={t('noPlatesYet')} hint={t('empty.hint')}
          action={isLab ? (
            <button type="button" onClick={toggleNewPlateForm} style={btnStyle('var(--c-primary)')}
                    data-testid="plates-empty-create" title={t('tip.newPlate')}>
              {t('empty.action')}
            </button>
          ) : null} />
      )}

      {!loading && !error && plates.length > 0 && (
        <button type="button" onClick={() => setTableExpanded(e => !e)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary-fg)', fontSize: 14, fontWeight: 600, padding: '4px 0', marginBottom: 8 }} title={t('tip.toggleRecords')}>
          {tableExpanded ? t('table.hideRecords', { count: plates.length }) : t('table.showRecords', { count: plates.length })}
        </button>
      )}

      {!loading && !error && tableExpanded && plates.length > 0 && (
          <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr style={{ background: 'var(--c-bg-muted-2)' }}>
                {[t('table.isolateId'), t('table.labCode'), t('table.plateCode'), t('table.protocol'), t('table.temp'), t('table.status'), t('table.sample'), t('table.created'), t('table.lastCheck')].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plates.map(p => (
                <tr key={p.plate_id} onClick={() => openDetail(p)}
                  style={{ borderBottom: '1px solid var(--c-bg-muted-3)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--c-bg-subtle)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''} title={t('tip.openPlate')}>
                  {/* The Isolate ID (the specimen the plate descends from) and its lab code lead. */}
                  <CodeCell code={p.specimen_code} testId="plate-specimen-code" />
                  <CodeCell code={p.lab_code} testId="plate-lab-code" />
                  <td style={tdStyle} dir="ltr">{p.plate_code}</td>
                  <td style={tdStyle} dir="ltr">{p.protocol}</td>
                  <td style={tdStyle}>{p.temperature_c ?? '—'}</td>
                  <td style={tdStyle}>{badge(p.status, t)}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }} dir="ltr">
                    {p.qr_uid || (p.sample_id ? p.sample_id.slice(0, 8) + '…' : p.external_id ? 'ext:' + p.external_id.slice(0, 8) + '…' : '—')}
                  </td>
                  <td style={tdStyle}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                  <td style={tdStyle}>{p.last_check_date ? new Date(p.last_check_date).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div style={overlayStyle} onClick={closeModal}>
          <div role="dialog" aria-modal="true" style={modalStyle} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--c-primary-fg)' }} dir="ltr">{detail.plate_code}</h3>
                <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', fontFamily: 'monospace' }} dir="ltr">{detail.plate_id}</span>
              </div>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)' }} title={t('tip.close')}>×</button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <QRCodeLabel value={detail.plate_code} caption={detail.isolate_code || ''} />
              {detail.gps_lat != null && detail.gps_lng != null && (
                <div style={{ fontSize: 13, marginTop: 6 }} data-testid="plate-detail-gps">
                  {t('newPlate.gpsLat')}/{t('newPlate.gpsLng')}: <span dir="ltr">{detail.gps_lat}, {detail.gps_lng}</span>
                </div>
              )}
            </div>

            {/* Plate info */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '6px 24px', fontSize: 14, marginBottom: 4 }}>
              {[
                [t('detail.protocol'),    detail.protocol, 'ltr'],
                ['__temperature__', null],
                ['__status__',      null],
                [t('detail.created'),     detail.created_at ? new Date(detail.created_at).toLocaleDateString() : null],
                [t('detail.lastCheck'),  detail.last_check_date ? new Date(detail.last_check_date).toLocaleDateString() : null],
                [t('detail.sample'),      detail.sample_id ? detail.sample_id.slice(0, 8) + '…' : null, 'ltr'],
                [t('detail.external'),    detail.external_id ? detail.external_id.slice(0, 8) + '…' : null, 'ltr'],
                [t('detail.transferredFrom'), detail.transferred_from_plate_code, 'ltr'],
                [t('detail.sourceSlant'), detail.source_slant_code, 'ltr'],
                [t('newPlate.donorNameLabel'), detail.donor_name],
                [t('newPlate.donorContactLabel'), detail.donor_contact],
                [t('newPlate.receivedDateLabel'), detail.received_date ? new Date(detail.received_date).toLocaleDateString() : null],
              ].map(([label, val, dir]) => {
                if (label === '__status__') return (
                  <div key="Status"><span style={{ color: 'var(--c-text-muted-fg)' }}>{t('detail.status')}: </span>{badge(detail.status, t)}</div>
                );
                if (label === '__temperature__') return (
                  <div key="Temperature">
                    <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('detail.temperature')}: </span>
                    {editingTemperature ? (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <input type="number" value={temperatureDraft} onChange={e => setTemperatureDraft(e.target.value)}
                          style={{ ...inputStyle, width: 70, padding: '2px 6px' }} title={t('tip.temperatureDraft')} />
                        <button onClick={saveTemperature} disabled={temperatureSaving}
                          style={{ ...btnStyle('var(--c-primary)'), padding: '2px 8px', fontSize: 11 }} title={t('tip.saveTemperature')}>
                          {temperatureSaving ? t('common:saving') : t('common:save')}
                        </button>
                        <button onClick={() => setEditingTemperature(false)}
                          style={{ ...btnStyle('var(--c-text-muted)'), padding: '2px 8px', fontSize: 11 }} title={t('tip.cancelEdit')}>{t('common:cancel')}</button>
                      </span>
                    ) : (
                      <>
                        {detail.temperature_c != null ? t('detail.temperatureValue', { value: detail.temperature_c }) : '—'}
                        {canEditNotes && (
                          <button onClick={() => { setTemperatureDraft(detail.temperature_c != null ? String(detail.temperature_c) : ''); setEditingTemperature(true); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary-fg)', fontSize: 11, marginInlineStart: 6, padding: 0, textDecoration: 'underline' }} title={t('tip.editTemperature')}>
                            {t('common:edit')}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
                return val != null ? (
                  <div key={label}><span style={{ color: 'var(--c-text-muted-fg)' }}>{label}: </span><span dir={dir}>{val}</span></div>
                ) : null;
              })}
            </div>
            <div style={{ marginTop: 12, fontSize: 13 }}>
              <PlateStartEditor plate={detail} canEdit={canEditNotes}
                                onSaved={(patch) => { setDetail(d => (d ? { ...d, ...patch } : d)); load(); }} />
            </div>

            {/* Notes */}
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <strong style={{ fontSize: 14 }}>{t('detail.notesTitle')}</strong>
                {canEditNotes && !editingNotes && (
                  <button onClick={() => { setNotesDraft(detail.notes || ''); setEditingNotes(true); }} style={{ ...btnStyle('var(--c-primary)'), padding: '4px 10px', fontSize: 12 }} title={t('tip.editNotes')}>{t('common:edit')}</button>
                )}
              </div>
              {editingNotes ? (
                <div>
                  <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)} rows={3} style={{ ...inputStyle, width: '100%' }} title={t('tip.notesDraft')} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button onClick={saveNotes} disabled={notesSaving} style={{ ...btnStyle('var(--c-primary)'), padding: '5px 12px', fontSize: 13 }} title={t('tip.saveNotes')}>
                      {notesSaving ? t('common:saving') : t('common:save')}
                    </button>
                    <button onClick={() => setEditingNotes(false)} style={{ ...btnStyle('var(--c-text-muted)'), padding: '5px 12px', fontSize: 13 }} title={t('tip.cancelEdit')}>{t('common:cancel')}</button>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: 13, color: detail.notes ? 'var(--c-text-body-fg)' : 'var(--c-text-faint-fg)', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {detail.notes || t('detail.noNotes')}
                </p>
              )}
            </div>

            {/* Check History */}
            <div style={{ marginTop: 20, borderTop: '1px solid var(--c-border)', paddingTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <strong style={{ fontSize: 14 }}>{t('detail.checkHistoryTitle')}</strong>
                {isLab && (
                  <button onClick={() => { setShowCheckForm(s => !s); setCheckError(''); setCheckForm(EMPTY_CHECK); setTransferMsg(''); }} style={{ ...btnStyle('var(--c-primary)'), padding: '5px 12px', fontSize: 13 }} title={t('tip.addCheck')}>
                    {showCheckForm ? t('common:cancel') : t('detail.addCheckBtn')}
                  </button>
                )}
              </div>

              {transferMsg && (
                <div style={{ background: 'var(--c-primary-soft)', border: '1px solid var(--c-primary-border-2)', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 13, color: 'var(--c-success-2-fg)' }}>
                  ✓ {transferMsg}
                </div>
              )}

              {showCheckForm && (
                <form onSubmit={submitCheck} style={subFormStyle}>
                  {checkError && <p role="alert" style={errStyle}>{checkError}</p>}
                  <div style={gridStyle}>
                    <label style={labelStyle}>
                      {t('detail.statusLabel')}
                      <select name="status" value={checkForm.status} onChange={handleCheckChange} style={inputStyle} title={t('tip.checkStatus')}>
                        <option value="">{t('common:selectPlaceholder')}</option>
                        {PLATE_STATUSES.map(s => <option key={s} value={s}>{t(`plateStatus.${s}`)}</option>)}
                      </select>
                    </label>
                    {isContaminated(checkForm.status) && (
                      <label style={labelStyle}>
                        {t('detail.contaminationTypeLabel')}
                        <select name="contamination_type" value={checkForm.contamination_type} onChange={handleCheckChange} style={inputStyle} title={t('tip.contaminationType')}>
                          <option value="">{t('common:selectPlaceholder')}</option>
                          {CONTAMINATION_TYPES.map(ct => <option key={ct.value} value={ct.value}>{t(`contaminationType.${ct.value}`)}</option>)}
                        </select>
                      </label>
                    )}
                    {checkForm.status === 'ready_to_transfer' && (
                      <label style={labelStyle}>
                        {t('detail.transferToLabel')}
                        <select name="transfer_to" value={checkForm.transfer_to} onChange={handleCheckChange} style={inputStyle} title={t('tip.transferTo')}>
                          <option value="">{t('common:selectPlaceholder')}</option>
                          <option value="plate">{t('detail.transferToPlate')}</option>
                          <option value="slant">{t('detail.transferToSlant')}</option>
                        </select>
                      </label>
                    )}
                    <label style={labelStyle}>
                      {t('detail.myceliumDiameterLabel')}
                      <input name="colony_diameter_mm" type="number" step="0.1" min="0" value={checkForm.colony_diameter_mm} onChange={handleCheckChange} style={inputStyle} title={t('tip.diameter')} />
                    </label>
                    <PlateConditionsFields values={checkForm} onChange={setCheckForm}
                      labelStyle={labelStyle} inputStyle={inputStyle} />
                    <label style={{ ...labelStyle, gridColumn: 'span 2' }}>
                      {t('common:notes')}
                      <textarea name="notes" value={checkForm.notes} onChange={handleCheckChange} rows={2} style={inputStyle} title={t('tip.checkNotes')} />
                    </label>
                  </div>
                  <button type="submit" disabled={checkSaving} style={btnStyle('var(--c-primary)')} title={t('tip.logCheck')}>
                    {checkSaving ? t('common:saving') : t('detail.logCheckBtn')}
                  </button>
                </form>
              )}

              {checksLoading ? (
                <PanelLoader t={t} />
              ) : checks.length === 0 ? (
                <p style={dimStyle}>{t('detail.noChecksYet')}</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                  <thead>
                    <tr style={{ background: 'var(--c-bg-muted-2)' }}>
                      {[t('detail.colDate'), t('detail.colStatus'), t('detail.colContamination'), t('detail.colDiameter'), t('conditions.column'), t('detail.colNotes')].map(h => (
                        <th key={h} style={{ ...thStyle, padding: '6px 10px', fontSize: 12 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {checks.map(c => (
                      <tr key={c.check_id} style={{ borderBottom: '1px solid var(--c-bg-muted-2)' }}>
                        <td style={{ ...tdStyle, padding: '6px 10px' }}>{c.check_date ? new Date(c.check_date).toLocaleDateString() : '—'}</td>
                        <td style={{ ...tdStyle, padding: '6px 10px' }}>{badge(c.status, t)}</td>
                        <td style={{ ...tdStyle, padding: '6px 10px' }}>{c.contamination_type ? t(`contaminationType.${c.contamination_type}`) : '—'}</td>
                        <td style={{ ...tdStyle, padding: '6px 10px' }}>{c.colony_diameter_mm ?? '—'}</td>
                        <td style={{ ...tdStyle, padding: '6px 10px' }} data-testid="check-conditions">{conditionsSummary(c, t) || '—'}</td>
                        <td style={{ ...tdStyle, padding: '6px 10px', color: 'var(--c-text-muted-fg)' }}>{c.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Photos */}
            <PhotoSection
              fetchUrl={`/plates/${detail.plate_id}/photos`}
              uploadBody={{ plate_id: detail.plate_id }}
              taxonName={detail.taxon_name}
              sourceType="lab"
              photoTypes={LAB_PHOTO_TYPES}
            />

            {/* Parallels */}
            <div style={{ marginTop: 20, borderTop: '1px solid var(--c-border)', paddingTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <strong style={{ fontSize: 14 }}>{t('detail.parallelPlatesTitle')}</strong>
                {isLab && (
                  <button onClick={() => { setShowParallelForm(s => !s); setParallelError(''); setParallelForm(EMPTY_PARALLEL); }} style={{ ...btnStyle('var(--c-primary)'), padding: '5px 12px', fontSize: 13 }} title={t('tip.linkParallel')}>
                    {showParallelForm ? t('common:cancel') : t('detail.linkParallelBtn')}
                  </button>
                )}
              </div>

              {showParallelForm && (
                <form onSubmit={submitParallel} style={subFormStyle}>
                  {parallelError && <p role="alert" style={errStyle}>{parallelError}</p>}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                    <label style={labelStyle}>
                      {t('detail.parallelPlateIdLabel')}
                      <input
                        name="parallel_id"
                        value={parallelForm.parallel_id}
                        onChange={e => setParallelForm({ parallel_id: e.target.value })}
                        placeholder={t('detail.parallelPlateIdPlaceholder')}
                        style={{ ...inputStyle, width: 300, fontFamily: 'monospace', fontSize: 12 }}
                      title={t('tip.parallelId')} />
                    </label>
                    <button type="submit" disabled={parallelSaving} style={{ ...btnStyle('var(--c-primary)'), alignSelf: 'flex-end' }} title={t('tip.link')}>
                      {parallelSaving ? t('common:saving') : t('detail.linkBtn')}
                    </button>
                  </div>
                </form>
              )}

              {parallelsLoading ? (
                <PanelLoader t={t} />
              ) : parallels.length === 0 ? (
                <p style={dimStyle}>{t('detail.noParallelsLinked')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  {parallels.map(pl => {
                    const otherId = pl.plate_id === detail.plate_id ? pl.parallel_id : pl.plate_id;
                    const otherPlate = plates.find(p => p.plate_id === otherId);
                    return (
                      <div key={pl.id} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--c-text-body-fg)' }} dir="ltr">
                          {otherPlate ? otherPlate.plate_code : otherId.slice(0, 8) + '…'}
                        </span>
                        {otherPlate && badge(otherPlate.status, t)}
                        {isLab && (
                          <button onClick={() => setDeleteParallelTarget(pl.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-faint-fg)', fontSize: 13 }} title={t('tip.removeParallel')}>×</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Related plates/slants — everything else from the same sample_id/external_id,
                not just this plate's own declared plate_parallels pair. */}
            <div style={{ marginTop: 20, borderTop: '1px solid var(--c-border)', paddingTop: 16 }}>
              <strong style={{ fontSize: 14 }}>{t('detail.relatedTitle')}</strong>
              {relatedLoading ? (
                <PanelLoader t={t} />
              ) : relatedPlates.length === 0 && relatedSlants.length === 0 ? (
                <p style={dimStyle}>{t('detail.noRelated')}</p>
              ) : (
                <div style={{ marginTop: 8 }}>
                  {relatedPlates.length > 0 && (
                    <div style={{ marginBottom: relatedSlants.length > 0 ? 10 : 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', marginBottom: 4 }}>{t('detail.relatedPlatesLabel')}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {relatedPlates.map(p => (
                          <button key={p.plate_id} onClick={() => openRelatedPlate(p.plate_id)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--c-border-strong)', borderRadius: 14, padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--c-primary-fg)' }} title={t('tip.openRelatedPlate')}>
                            <span dir="ltr">{p.plate_code}</span>
                            {badge(p.status, t)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {relatedSlants.length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', marginBottom: 4 }}>{t('detail.relatedSlantsLabel')}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {relatedSlants.map(s => {
                          const slantColor = SLANT_STATUS_COLORS[s.status] || { background: 'var(--c-bg-muted)', color: 'var(--c-text-body-fg)' };
                          return (
                            <button key={s.slant_id} onClick={() => openRelatedSlant(s.slant_id)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--c-border-strong)', borderRadius: 14, padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--c-primary-fg)' }} title={t('tip.openRelatedSlant')}>
                              <span dir="ltr">{s.slant_code}</span>
                              <span style={{ ...slantColor, padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                {t(`slantStatus.${s.status}`, { ns: 'slants' })}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {canAssign && (
              <div style={{ marginTop: 20, borderTop: '1px solid var(--c-border)', paddingTop: 16 }}>
                <strong style={{ fontSize: 14 }}>{t('detail.assignedToTitle')}</strong>
                {assignmentsLoading ? (
                  <PanelLoader t={t} />
                ) : (
                  <div style={{ marginTop: 8 }}>
                    {plateAssignments.length === 0 ? (
                      <p style={dimStyle}>{t('detail.noAssignments')}</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                        {plateAssignments.map(a => (
                          <div key={a.assignment_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--c-bg-muted)', borderRadius: 6, padding: '6px 12px', fontSize: 13 }}>
                            <span>{a.username}</span>
                            <button onClick={() => removeAssignment(a.assignment_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-faint-fg)', fontSize: 13 }} title={t('tip.removeAssignment')}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {assignError && <p role="alert" style={errStyle}>{assignError}</p>}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                      <select value={assignUserId} onChange={e => setAssignUserId(e.target.value)} style={{ ...inputStyle, flex: 1 }} title={t('tip.assignUser')}>
                        <option value="">{t('detail.assignSelectPlaceholder')}</option>
                        {allUsers.map(u => (
                          <option key={u.user_id} value={u.user_id}>{u.username}</option>
                        ))}
                      </select>
                      <button onClick={submitAssignment} disabled={assignSaving || !assignUserId} style={{ ...btnStyle('var(--c-primary)'), padding: '5px 12px', fontSize: 13 }} title={t('tip.assign')}>
                        {assignSaving ? t('common:saving') : t('detail.assignBtn')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Delete: at the foot, away from the × (DangerZone). Plates are
                lab data: a collection manager's or a lab manager's to delete. */}
            {canDelete && (
              <DangerZone testId="plate-delete" label={t('common:delete')} title={t('tip.delete')}
                confirmTitle={t('detail.deleteConfirmTitle')}
                consequences={[t('detail.deleteConsequence1'), t('detail.deleteConsequence2')]}
                failedText={t('detail.deleteFailed')} onConfirm={deletePlate} />
            )}
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={!!deleteParallelTarget}
        title={t('detail.removeParallelConfirmTitle')}
        deleting={deletingParallel}
        onConfirm={confirmRemoveParallel}
        onCancel={() => setDeleteParallelTarget(null)}
      />
    </div>
  );
}

const thStyle = { padding: '8px 12px', textAlign: 'start', fontWeight: 600 };
const tdStyle = { padding: '8px 12px' };
const codeStyle = { fontFamily: 'monospace', fontSize: 12 };

// An isolate's code in a table cell: monospace, left to right in Hebrew too,
// and '—' when there is none.
function CodeCell({ code, testId }) {
  return (
    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} data-testid={testId}>
      {code ? <bdi dir="ltr" style={codeStyle}>{code}</bdi> : '—'}
    </td>
  );
}

const dimStyle = { fontSize: 13, color: 'var(--c-text-faint-fg)' };
// A refusal said once, in the theme's danger ink, and announced: focus stays
// on the button that was pressed, so nothing else would say it.
const errStyle = { color: 'var(--c-danger-fg)', fontSize: 13, margin: '0 0 10px' };

const smallBtn = (bg) => ({
  background: bg,
  color: 'var(--c-on-accent)',
  border: 'none',
  borderRadius: 4,
  padding: '4px 10px',
  cursor: 'pointer',
  fontSize: 12,
});
const subFormStyle = { background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8, padding: 14, marginBottom: 12 };

const formStyle = {
  background: 'var(--c-bg-subtle)',
  border: '1px solid var(--c-border)',
  borderRadius: 8,
  padding: 20,
  marginBottom: 24,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 14,
  marginBottom: 16,
};

// A labelled picker with its message under it, outside the <label>.
const fieldStackStyle = { display: 'flex', flexDirection: 'column', gap: 2 };

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 14,
  fontWeight: 500,
};

const inputStyle = {
  padding: '6px 10px',
  border: '1px solid var(--c-border-strong)',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 400,
};

const btnStyle = (bg) => ({
  background: bg,
  color: 'var(--c-on-accent)',
  border: 'none',
  borderRadius: 6,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: 14,
});

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: 'var(--c-surface)',
  borderRadius: 10,
  padding: 28,
  width: 700,
  maxWidth: '92vw',
  maxHeight: '88vh',
  overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};
